import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProductAddToCart } from "../../../../../cart/hooks/useProductAddToCart";
import type { TransformedProduct } from "../../../../types/productApiTypes";
import QuantityControl from "../../../../../cart/components/common/QuantityControl";
import Icon from "../../../../../../../common/components/Icon/Icon";
import { useOpenStockNotificationModal } from "../../../../../stockNotification/hooks/useOpenStockNotificationModal";

interface ProductActionsProps {
  product: TransformedProduct;
  selectedVariation: TransformedProduct | null;
  selectedAttributes: { [key: string]: string };
  showDetailsLink?: boolean;
}

const ProductActions: React.FC<ProductActionsProps> = ({
  product,
  selectedVariation,
  selectedAttributes,
  showDetailsLink = true,
}) => {
  const { t } = useTranslation();
  const { handleAddToCart, isAddingToCart } = useProductAddToCart();
  const openStockModal = useOpenStockNotificationModal();

  // B2B Approval check
  const isApprovedMember = window.commonVariables?.isApprovedMember ?? false;
  const loginUrl = window.commonVariables?.loginUrl || "/login";

  const displayData = selectedVariation || product;
  const isSoldIndividually = displayData.soldIndividually;
  const minQuantity = displayData.minStock || 1;
  const maxQuantity = isSoldIndividually
    ? 1
    : Math.min(displayData.maxStock ?? 100, 100);

  const [quantity, setQuantity] = useState(minQuantity);

  useEffect(() => {
    const currentIsSoldIndividually =
      selectedVariation?.soldIndividually || product.soldIndividually;

    const newMin = (selectedVariation?.minStock || product.minStock) ?? 1;
    const newMax = currentIsSoldIndividually
      ? 1
      : Math.min((selectedVariation?.maxStock || product.maxStock) ?? 100, 100);

    setQuantity((currentQty) => Math.max(newMin, Math.min(currentQty, newMax)));
  }, [selectedVariation, product]);

  const handleQuantityChange = (newValue: number) => {
    setQuantity(newValue);
  };

  // --- B2B CATALOG MODE: Replace purchasing options with Login prompt ---
  if (!isApprovedMember) {
    return (
      <div className="product-info-details__actions">
        <div
          className="product-info-details__login-prompt-box"
          style={{
            padding: "1.25rem",
            backgroundColor: "#f8f9fa",
            border: "1px solid #e9ecef",
            borderRadius: "8px",
            textAlign: "center",
            marginBottom: "1rem",
          }}
        >
          <p
            style={{
              margin: "0 0 1rem 0",
              fontSize: "14px",
              fontWeight: "500",
              color: "#333",
            }}
          >
            {t(
              "productInfoDetails.loginRequiredNotice",
              "Fiyatları görmek ve sipariş verebilmek için üye girişi yapmanız gerekmektedir.",
            )}
          </p>
          <a
            href={loginUrl}
            className="button button--primary"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "0.75rem 1.5rem",
              borderRadius: "6px",
              textDecoration: "none",
            }}
          >
            <Icon iconName="user" className="icon--stroke-thick" />
            <span>
              {t("productInfoDetails.loginToOrder", "Giriş Yap / Üye Ol")}
            </span>
          </a>
        </div>

        {showDetailsLink && (
          <a
            href={product.permalink}
            className="product-info-details__view-details-btn"
          >
            {t("productInfoDetails.viewFullDetails")}
          </a>
        )}
      </div>
    );
  }

  if (product.type === "external") {
    return (
      <div className="product-info-details__actions">
        <a
          href={product.addToCartUrl}
          className="product-info-details__external-btn"
          target="_blank"
          rel="noopener noreferrer"
          title={product.addToCartText}
        >
          <span>{t("productInfoDetails.buyProduct")}</span>
          <Icon iconName="external-link" className="icon--stroke-thick" />
        </a>
      </div>
    );
  }

  const isPurchasable = displayData.purchasable;
  const isOutOfStock = displayData.stockStatus === "outofstock";
  const isOnBackorder = displayData.onBackorder;

  let warningMessage: string | null = null;
  let isSelectionIncomplete = false;
  let isCombinationInvalid = false;

  if (product.isVariable) {
    const requiredAttributeCount = product.enrichedAttributes.filter(
      (attr) => attr.hasVariations,
    ).length;
    const selectedAttributeCount = Object.keys(selectedAttributes).length;

    isSelectionIncomplete = selectedAttributeCount < requiredAttributeCount;
    isCombinationInvalid = !isSelectionIncomplete && !selectedVariation;

    if (isSelectionIncomplete) {
      warningMessage = t("productInfoDetails.selectAttributesMessage");
    } else if (isCombinationInvalid) {
      warningMessage = t("productInfoDetails.invalidCombinationMessage");
    }
  }

  if (!warningMessage && !isPurchasable && !isOutOfStock) {
    warningMessage = t(
      "productInfoDetails.unavailableMessage",
      "This product is currently unavailable.",
    );
  }

  const isAddToCartDisabled =
    isAddingToCart ||
    !isPurchasable ||
    (isOutOfStock && !isOnBackorder) ||
    isSelectionIncomplete ||
    isCombinationInvalid;

  const onAddToCartClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    let productIdToAdd = product.id;
    let variationData: { attribute: string; value: string }[] | undefined;

    if (product.isVariable) {
      if (!selectedVariation) return;
      productIdToAdd = selectedVariation.id;
      variationData = Object.entries(selectedAttributes).map(
        ([attribute, value]) => ({
          attribute,
          value,
        }),
      );
    }

    handleAddToCart(e, {
      productId: productIdToAdd,
      quantity: quantity,
      variation: variationData,
    });
  };

  const handleNotifyMe = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const variationId = selectedVariation ? selectedVariation.id : undefined;
    const displayName = selectedVariation
      ? `${product.name} - ${selectedVariation.name}`
      : product.name;

    openStockModal({
      productId: product.id,
      variationId,
      productName: displayName,
    });
  };

  return (
    <div className="product-info-details__actions">
      {warningMessage && (
        <div className="product-info-details__actions-warning">
          {warningMessage}
        </div>
      )}

      <div className="product-info-details__actions-main">
        {isOutOfStock && !isOnBackorder && !isSelectionIncomplete ? (
          <div className="product-info-details__notify-wrapper">
            <button
              className="product-info-details__notify-btn"
              onClick={handleNotifyMe}
            >
              <Icon iconName="bell" className="icon--stroke-thickest" />
              <span>{t("productInfoDetails.notifyMe", "Notify me")}</span>
            </button>
          </div>
        ) : (
          <div className="product-info-details__purchase-actions">
            <QuantityControl
              value={quantity}
              onChange={handleQuantityChange}
              min={minQuantity}
              max={maxQuantity}
              disabled={isAddToCartDisabled || isSoldIndividually}
              ariaLabel={t("productInfoDetails.quantityLabel", {
                productName: product.name,
              })}
              className="product-info-details__quantity-selector"
            />
            <button
              className="product-info-details__add-to-cart-btn"
              onClick={onAddToCartClick}
              disabled={isAddToCartDisabled}
            >
              {isAddingToCart ? (
                t("productInfoDetails.addingToCart")
              ) : !isPurchasable ? (
                <>
                  <span>
                    {t("productInfoDetails.unavailable", "Unavailable")}
                  </span>
                  <Icon iconName="info-outlined" className="icon--filled" />
                </>
              ) : isOutOfStock && !isOnBackorder ? (
                <>
                  <span>{t("productInfoDetails.outOfStock")}</span>
                  <Icon iconName="not-allowed" className="icon--filled" />
                </>
              ) : isOnBackorder ? (
                <>
                  <span>{t("productInfoDetails.backorder", "Backorder")}</span>
                  <Icon
                    iconName="shopping-cart-filled"
                    className="icon--filled"
                  />
                </>
              ) : (
                <>
                  <span>{t("productInfoDetails.addToCartLong")}</span>
                  <Icon
                    iconName="shopping-cart-filled"
                    className="icon--filled"
                  />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {showDetailsLink && (
        <a
          href={product.permalink}
          className="product-info-details__view-details-btn"
        >
          {t("productInfoDetails.viewFullDetails")}
        </a>
      )}
    </div>
  );
};

export default ProductActions;
